/* ==========================================================================
   TYLPS — Animations de conversion
   Diaporama, titres mot à mot, compteurs animés, envol vers le panier,
   fenêtres d'information. Chargé après tylps-global.js.
   ========================================================================== */
(() => {
  'use strict';

  const T = (window.TYLPS = window.TYLPS || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const body = () => document.body;
  const on = (cls) => body().classList.contains(cls) && !reduceMotion;

  /* ==========================================================================
     Diaporama
     ========================================================================== */
  class TylpsSlideshow extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.slides = $$('.tylps-slide', this);
      this.tabs = $$('[data-goto]', this);
      this.index = 0;
      this.duration = (parseFloat(this.dataset.speed) || 7) * 1000;
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion && this.slides.length > 1;
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.go(parseInt(tab.dataset.goto, 10), true)));
      $('[data-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
      $('[data-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
      this.pauseBtn = $('[data-pause]', this);
      this.pauseBtn?.addEventListener('click', () => (this.paused ? this.play() : this.pause(true)));
      if (!this.autoplay) this.pauseBtn?.setAttribute('hidden', '');

      // Glisser au doigt
      let x0 = null;
      this.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1), true);
        x0 = null;
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.go(this.index + 1, true);
        if (e.key === 'ArrowLeft') this.go(this.index - 1, true);
      });

      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible && this.autoplay && !this.paused) this.play();
        else this.stopTimer();
      }, { threshold: 0.25 });
      this.io.observe(this);
      this.setActive(0, true);
    }
    disconnectedCallback() {
      this.stopTimer();
      this.io?.disconnect();
    }
    go(i, user) {
      const n = this.slides.length;
      if (!n) return;
      const next = ((i % n) + n) % n;
      if (next === this.index) return;
      this.setActive(next);
      if (user && this.autoplay && !this.paused) this.play();
    }
    setActive(i, first) {
      this.slides.forEach((s, j) => {
        const active = j === i;
        s.classList.toggle('is-active', active);
        s.classList.toggle('is-leaving', !active && j === this.index && !first);
        s.setAttribute('aria-hidden', String(!active));
        const v = s.querySelector('video');
        if (v) active ? v.play().catch(() => {}) : v.pause();
      });
      this.tabs.forEach((t, j) => {
        t.classList.toggle('is-active', j === i);
        t.setAttribute('aria-selected', String(j === i));
        t.classList.remove('is-running');
      });
      setTimeout(() => this.slides.forEach((s) => s.classList.remove('is-leaving')), 1200);
      this.index = i;
    }
    play() {
      this.paused = false;
      this.classList.remove('is-paused');
      this.pauseBtn?.classList.remove('is-paused');
      this.stopTimer();
      if (!this.visible) return;
      const tab = this.tabs[this.index];
      if (tab) {
        tab.classList.remove('is-running');
        void tab.offsetWidth;
        tab.classList.add('is-running');
      }
      this.timer = setTimeout(() => {
        this.go(this.index + 1);
        this.play();
      }, this.duration);
    }
    pause(user) {
      this.paused = !!user;
      this.classList.add('is-paused');
      this.pauseBtn?.classList.add('is-paused');
      this.stopTimer();
      this.tabs.forEach((t) => t.classList.remove('is-running'));
    }
    stopTimer() {
      clearTimeout(this.timer);
    }
  }
  if (!customElements.get('tylps-slideshow')) customElements.define('tylps-slideshow', TylpsSlideshow);

  /* ==========================================================================
     Titres mot à mot
     ========================================================================== */
  function splitHeadings(root = document) {
    if (!on('tylps-split-on') || !on('tylps-anim')) return;
    const sel = '.tylps-section h2:not([data-split]):not(.tylps-slide__heading):not(.tylps-hero__heading)';
    $$(sel, root).forEach((h) => {
      if (h.closest('.tylps-no-anim, .tylps-drawer, dialog, .tylps-header, .tylps-footer')) return;
      // On ne découpe que du texte simple (avec d'éventuels <br>)
      if ([...h.childNodes].some((n) => n.nodeType === 1 && n.tagName !== 'BR')) return;
      h.setAttribute('data-split', '');
      h.setAttribute('aria-label', h.textContent.trim());
      let i = 0;
      const frag = document.createDocumentFragment();
      [...h.childNodes].forEach((node) => {
        if (node.nodeType === 1) {
          frag.appendChild(node.cloneNode());
          return;
        }
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(' '));
            return;
          }
          const w = document.createElement('span');
          w.className = 'tylps-word';
          w.setAttribute('aria-hidden', 'true');
          w.style.setProperty('--w', i++);
          const inner = document.createElement('span');
          inner.textContent = part;
          w.appendChild(inner);
          frag.appendChild(w);
        });
      });
      h.textContent = '';
      h.appendChild(frag);
      splitObserver.observe(h);
    });
  }
  const splitObserver = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-split-in');
        splitObserver.unobserve(e.target);
      }),
    { rootMargin: '0px 0px -8% 0px' }
  );

  /* ==========================================================================
     Compteurs animés
     ========================================================================== */
  const locale = () => T.locale || document.documentElement.lang || 'fr';
  function formatNumber(n, decimals) {
    return n.toLocaleString(locale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  function initCountups(root = document) {
    $$('[data-countup]:not([data-counted])', root).forEach((el) => {
      const target = parseFloat(String(el.dataset.countup).replace(',', '.'));
      if (isNaN(target)) return;
      const decimals = parseInt(el.dataset.decimals || '0', 10) || (String(el.dataset.countup).includes('.') ? 1 : 0);
      const suffix = el.dataset.suffix || '';
      el.setAttribute('data-counted', '');
      const final = formatNumber(target, decimals) + suffix;
      if (!on('tylps-countup-on') || T.designMode) {
        el.textContent = final;
        return;
      }
      el.style.minWidth = `${final.length * 0.62}ch`;
      el.textContent = formatNumber(0, decimals) + suffix;
      countObserver.observe(el);
      el._count = { target, decimals, suffix, final };
    });
  }
  const countObserver = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        countObserver.unobserve(e.target);
        const { target, decimals, suffix, final } = e.target._count;
        const start = performance.now();
        const dur = 1600;
        const step = (now) => {
          const p = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - p, 4);
          e.target.textContent = p < 1 ? formatNumber(target * eased, decimals) + suffix : final;
          if (p < 1) requestAnimationFrame(step);
          else e.target.style.minWidth = '';
        };
        requestAnimationFrame(step);
      }),
    { threshold: 0.6 }
  );

  /* ==========================================================================
     Envol du produit vers le panier
     ========================================================================== */
  function flyToCart(button) {
    if (!on('tylps-fly-on')) return;
    const scope = button.closest('tylps-product, .tylps-bundle, .tylps-product-card, .tylps-section') || document;
    const img =
      scope.querySelector('.tylps-gallery__slide.is-active img') ||
      scope.querySelector('.tylps-bundle__media img') ||
      scope.querySelector('img');
    const cart = $('.tylps-header [data-cart-count]')?.closest('a') || $('.tylps-header__actions a:last-child');
    if (!img || !cart) return;
    const from = img.getBoundingClientRect();
    const headerSection = cart.closest('.tylps-header-section, .shopify-section');
    const wasHidden = headerSection?.classList.contains('is-hidden');
    if (wasHidden) headerSection.classList.remove('is-hidden');
    const r = cart.getBoundingClientRect();
    const to = { left: r.left, width: r.width, height: r.height, top: r.top + (wasHidden ? headerSection.offsetHeight : 0) };
    if (!from.width || from.bottom < 0 || from.top > window.innerHeight) return;
    const size = Math.min(from.width, from.height, 260);
    const ghost = img.cloneNode();
    ghost.removeAttribute('srcset');
    ghost.removeAttribute('sizes');
    ghost.src = img.currentSrc || img.src;
    ghost.className = 'tylps-fly';
    Object.assign(ghost.style, {
      left: `${from.left + from.width / 2 - size / 2}px`,
      top: `${from.top + from.height / 2 - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
    });
    document.body.appendChild(ghost);
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    ghost
      .animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1, borderRadius: '18px' },
          { transform: `translate(${dx * 0.55}px, ${dy * 0.35 - 80}px) scale(0.45)`, opacity: 1, borderRadius: '40%', offset: 0.55 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.08)`, opacity: 0.2, borderRadius: '50%' },
        ],
        { duration: 820, easing: 'cubic-bezier(0.55, 0, 0.25, 1)' }
      )
      .finished.then(() => {
        ghost.remove();
        cart.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 420, easing: 'ease-out' });
      });
  }
  document.addEventListener('tylps:cart:added', (e) => {
    const btn = e.detail?.button;
    if (btn) flyToCart(btn);
  });

  /* ==========================================================================
     Fenêtres d'information (dimensions, garantie…)
     ========================================================================== */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const d = document.getElementById(opener.dataset.dialogOpen);
      if (d?.showModal) {
        d.showModal();
        document.documentElement.classList.add('tylps-lock');
      }
      return;
    }
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (e.target.matches?.('dialog.tylps-dialog')) e.target.close();
  });
  document.addEventListener(
    'close',
    (e) => {
      if (e.target.matches?.('dialog.tylps-dialog') && !$('dialog.tylps-dialog[open]')) document.documentElement.classList.remove('tylps-lock');
    },
    true
  );

  /* ==========================================================================
     Initialisation
     ========================================================================== */
  const init = (root) => {
    splitHeadings(root);
    initCountups(root);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    $$('h2[data-split]', e.target).forEach((h) => h.classList.add('is-split-in'));
  });
  document.addEventListener('shopify:section:select', (e) => $$('h2[data-split]', e.target).forEach((h) => h.classList.add('is-split-in')));
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.tylps-slide');
    const show = slide?.closest('tylps-slideshow');
    if (show) {
      show.pause(true);
      show.setActive(parseInt(slide.dataset.slide, 10));
    }
  });
})();

/* ==========================================================================
   Aide au choix du modèle : onglets, quiz et sélection de la variante
   ========================================================================== */
(() => {
  'use strict';
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  function showPane(dialog, name) {
    $$('[data-help-tab]', dialog).forEach((t) => t.setAttribute('aria-selected', String(t.dataset.helpTab === name)));
    $$('[data-help-pane]', dialog).forEach((p) => {
      const on = p.dataset.helpPane === name;
      p.hidden = !on;
      if (on) p.animate?.([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 350, easing: 'cubic-bezier(0.22, 0.75, 0.15, 1)' });
    });
  }

  function resetQuiz(quiz) {
    quiz._score = [0, 0];
    quiz._step = 0;
    const steps = $$('[data-quiz-step]', quiz);
    steps.forEach((s, i) => (s.hidden = i !== 0));
    quiz.querySelector('[data-quiz-result]').hidden = true;
    $$('[data-quiz-pick]', quiz).forEach((p) => (p.hidden = true));
    const bar = quiz.querySelector('[data-quiz-bar]');
    if (bar) bar.style.width = '0%';
  }

  function pickModel(dialog, value) {
    const scope = dialog.closest('tylps-product') || document;
    let done = false;
    $$('[data-variant-picker] input[type="radio"]', scope).forEach((input) => {
      if (done || input.value !== value) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      done = true;
    });
    if (!done) {
      $$('[data-variant-picker] select', scope).forEach((sel) => {
        if (done) return;
        if ([...sel.options].some((o) => o.value === value)) {
          sel.value = value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          done = true;
        }
      });
    }
    dialog.close();
    const picker = scope.querySelector('[data-variant-picker]');
    picker?.animate?.([{ transform: 'scale(1)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }], { duration: 450 });
  }

  document.addEventListener('click', (e) => {
    const dialog = e.target.closest('dialog[data-model-help]');
    if (!dialog) {
      const opener = e.target.closest('[data-dialog-open]');
      const d = opener && document.getElementById(opener.dataset.dialogOpen);
      if (d?.matches('[data-model-help]')) {
        showPane(d, d.querySelector('[data-help-pane="compare"]') ? 'compare' : 'quiz');
        const quiz = d.querySelector('.tylps-quiz');
        if (quiz) resetQuiz(quiz);
      }
      return;
    }
    const tab = e.target.closest('[data-help-tab]');
    if (tab) return showPane(dialog, tab.dataset.helpTab);

    const pick = e.target.closest('[data-pick-model]');
    if (pick) return pickModel(dialog, pick.dataset.pickModel);

    const quiz = e.target.closest('.tylps-quiz');
    if (!quiz) return;
    if (e.target.closest('[data-quiz-restart]')) return resetQuiz(quiz);
    const answer = e.target.closest('[data-quiz-answer]');
    if (!answer) return;
    if (!quiz._score) resetQuiz(quiz);
    quiz._score[parseInt(answer.dataset.quizAnswer, 10) - 1]++;
    answer.classList.add('is-chosen');
    const steps = $$('[data-quiz-step]', quiz);
    const bar = quiz.querySelector('[data-quiz-bar]');
    quiz._step++;
    if (bar) bar.style.width = `${(quiz._step / steps.length) * 100}%`;
    setTimeout(() => {
      answer.classList.remove('is-chosen');
      steps.forEach((s, i) => (s.hidden = i !== quiz._step));
      if (quiz._step >= steps.length) {
        const winner = quiz._score[1] > quiz._score[0] ? 2 : 1;
        quiz.querySelector('[data-quiz-result]').hidden = false;
        $$('[data-quiz-pick]', quiz).forEach((p) => (p.hidden = parseInt(p.dataset.quizPick, 10) !== winner));
      }
    }, 260);
  });
})();
